import React, { useState } from 'react'
import {
  ArrowLeftIcon,
  SearchIcon,
  DocumentTextIcon,
  PlusIcon,
  PlayIcon,
  PencilAltIcon,
  DuplicateIcon,
  RefreshIcon,
  TrashIcon
} from '@heroicons/react/outline'
import { StarIcon } from '@heroicons/react/solid'

// --- Helpers (même structure que NotificationHub) ---

function getTitle(translations, lang) {
  if (!translations || !translations.length)
    return '(Sans titre)'
  for (let i = 0; i < translations.length; i++) {
    if (translations[i].lang === lang && translations[i].title)
      return translations[i].title
  }
  return translations[0].title || '(Sans titre)'
}

function getBody(translations, lang) {
  if (!translations || !translations.length)
    return ''
  for (let i = 0; i < translations.length; i++) {
    if (translations[i].lang === lang && translations[i].content)
      return translations[i].content
  }
  return translations[0].content || ''
}

function getFilledLangs(translations) {
  if (!translations || !translations.length)
    return []
  return translations
    .filter(t => t.title || t.content)
    .map(t => t.lang)
}

function formatDate(ts) {
  if (!ts || ts === false)
    return '—'
  const d = new Date(ts * 1000)
  return d.toLocaleDateString('fr-FR')
}

// --- Composant ---

export default function TemplatesLibrary({
  templates,
  emptyStateActive,
  onSelectTemplate,
  onEditTemplate,
  onDuplicateTemplate,
  onDeleteTemplate,
  onNewTemplate,
  onBack
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)

  const displayedTemplates = emptyStateActive ? [] : (templates || [])

  const filteredTemplates = displayedTemplates.filter(t => {
    const q = searchQuery.toLowerCase()
    return (t.name || '').toLowerCase().includes(q) ||
      getTitle(t.translations, 'fr').toLowerCase().includes(q) ||
      getBody(t.translations, 'fr').toLowerCase().includes(q)
  })

  return (
    <div className="space-y-6 animate-fade-in" id="templates-library-view">

      {/* 1. En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-slate-100 pb-5 gap-3">
        <div className="flex items-center gap-3">
          <button
            id="template-lib-back-btn"
            onClick={onBack}
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-500 hover:text-slate-800"
            style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight" style={{ margin: 0 }}>
              Bibliothèque des modèles de notifications
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Gérez les modèles de notifications pré-validés pour simplifier l'envoi de push.
            </p>
          </div>
        </div>

        <button
          id="lib-new-template-btn"
          onClick={onNewTemplate}
          className="bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm px-4 py-2 rounded-xl transition-all flex items-center gap-1 flex-shrink-0"
          style={{ backgroundImage: 'none' }}
        >
          <PlusIcon className="w-4 h-4" />
          Créer un modèle
        </button>
      </div>

      {/* 2. Barre de recherche */}
      <div className={`bg-white p-3 rounded-2xl border flex items-center gap-2 transition-all ${searchFocused ? 'border-indigo-400 ring-2 ring-indigo-100' : 'border-slate-200'}`}>
        <SearchIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <input
          id="template-lib-search-input"
          type="text"
          placeholder="Rechercher par nom de template ou contenu textuel..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="browser-default w-full text-sm text-slate-700 bg-transparent py-1 border-0 focus:ring-0 focus:outline-none"
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="text-sm text-slate-400 hover:text-slate-600 font-mono"
            style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
          >
            [Effacer]
          </button>
        )}
      </div>

      {/* 3. Grille des modèles */}
      {filteredTemplates.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center" id="empty-lib-box">
          <DocumentTextIcon className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <h3 className="font-semibold text-sm text-slate-800">Aucun modèle de message répertorié</h3>
          <p className="text-slate-400 text-sm mt-1 max-w-md mx-auto leading-relaxed">
            {emptyStateActive
              ? "Tous les modèles ont été masqués via l'émulateur. Activez le mode normal pour retrouver les configurations par défaut."
              : "Aucun template ne correspond à votre recherche textuelle."}
          </p>
          {!emptyStateActive && searchQuery && (
            <button
              id="lib-reset-filter-btn"
              onClick={() => setSearchQuery('')}
              className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-semibold inline-flex items-center gap-1"
              style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
            >
              <RefreshIcon className="w-3 h-3" /> Réinitialiser
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredTemplates.map(tpl => {
            const id = tpl._id
            const title = getTitle(tpl.translations, 'fr')
            const body = getBody(tpl.translations, 'fr')
            const filledLangs = getFilledLangs(tpl.translations)

            return (
              <div
                key={id}
                id={`tpl-card-${id}`}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all p-5 flex flex-col justify-between"
              >
                <div>
                  {/* Badge + usage */}
                  <div className="flex justify-end items-start mb-2">
                    <span
                      className="text-slate-400 font-mono font-semibold flex items-center gap-1"
                      style={{ fontSize: '10px' }}
                    >
                      <StarIcon className="w-3 h-3" style={{ color: '#f59e0b' }} />
                      Usage : {tpl.usage_count || 0} fois
                    </span>
                  </div>

                  {/* Nom interne */}
                  <h3 className="font-bold text-indigo-900 text-sm uppercase mb-1" style={{ letterSpacing: '0.01em', margin: 0, marginBottom: '4px' }}>
                    {tpl.name || '(Sans nom)'}
                  </h3>

                  {/* Aperçu du contenu */}
                  <div className="bg-zinc-50 p-3 rounded-xl border border-indigo-100 mt-2 space-y-1">
                    <span className="font-bold text-slate-800 text-sm block">{title}</span>
                    <p className="text-slate-600 text-sm leading-relaxed line-clamp-3">
                      {body}
                    </p>
                  </div>

                  {/* Tags langues */}
                  {filledLangs.length > 0 && (
                    <div className="flex gap-1 items-center mt-3 flex-wrap">
                      <span
                        className="font-semibold font-mono text-slate-400"
                        style={{ fontSize: '10px' }}
                      >
                        Langues validées :
                      </span>
                      <div className="flex gap-1 flex-wrap">
                        {filledLangs.map(lang => (
                          <span
                            key={lang}
                            className="uppercase font-mono px-1 py-0.5 bg-slate-100 border border-slate-300 text-slate-600 rounded"
                            style={{ fontSize: '9px' }}
                          >
                            {lang}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer : date + actions */}
                <div className="mt-5 pt-3 border-t border-slate-100 flex items-center justify-between">
                  <span className="font-mono font-semibold text-indigo-400" style={{ fontSize: '11px' }}>
                    Enregistré le : {formatDate(tpl.create_time)}
                  </span>
                  <div className="flex gap-2 items-center">
                    <button
                      id={`tpl-dup-btn-${id}`}
                      onClick={() => onDuplicateTemplate(tpl)}
                      className="p-1 hover:bg-slate-100 text-slate-400 hover:text-slate-700 rounded-lg transition-colors"
                      title="Dupliquer le template"
                      style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                    >
                      <DuplicateIcon className="w-5 h-5" />
                    </button>
                    <button
                      id={`tpl-edit-btn-${id}`}
                      onClick={() => onEditTemplate(tpl)}
                      className="p-1 hover:bg-slate-100 text-slate-400 hover:text-slate-800 rounded-lg transition-colors"
                      title="Modifier le template"
                      style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                    >
                      <PencilAltIcon className="w-5 h-5" />
                    </button>
                    <button
                      id={`tpl-delete-btn-${id}`}
                      onClick={() => onDeleteTemplate(tpl)}
                      className="p-1 hover:bg-rose-50 text-slate-400 hover:text-rose-600 rounded-lg transition-colors border border-transparent"
                      title="Supprimer le template"
                      style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                    <button
                      id={`tpl-use-btn-${id}`}
                      onClick={() => onSelectTemplate(tpl)}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-3 py-1 rounded-lg transition-all flex items-center gap-1 shadow-sm text-sm"
                      style={{ backgroundImage: 'none' }}
                    >
                      <PlayIcon className="w-5 h-5" /> Utiliser
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

    </div>
  )
}
